// ============================================================================
// KLINISOL CRO - Forms Library
// File: src/pages/FormsLibrary.js
// Purpose: Dynamic Forms & Templates Library with Parent SOP linking
// FDA 21 CFR Part 11 Compliant
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
    FileText,
    Search,
    Filter,
    Eye,
    Download,
    ChevronDown,
    ChevronRight,
    ArrowLeft,
    RefreshCw,
    AlertTriangle,
    FileCheck,
    ClipboardList,
    Calendar,
    Link as LinkIcon,
    PowerOff,
    Home
} from 'lucide-react';

// Brand colors
const COLORS = {
    slateGrey: '#475569',
    magenta: '#E91E63'
};

const FormsLibrary = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // State
    const [forms, setForms] = useState([]);
    const [filteredForms, setFilteredForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedSOP, setSelectedSOP] = useState('all');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [stats, setStats] = useState({ total: 0, forms: 0, templates: 0 });

    // Categories
    const categories = [
        { value: 'all', label: 'All Categories' },
        { value: 'Clinical Operations', label: 'Clinical Operations (OPS)' },
        { value: 'Quality Management', label: 'Quality Management (QMS)' },
        { value: 'Data Management', label: 'Data Management (DMS)' },
        { value: 'Regulatory Affairs', label: 'Regulatory Affairs (REG)' },
        { value: 'Pharmacovigilance', label: 'Pharmacovigilance (PVG)' }
    ];

    // Load forms
    useEffect(() => {
        loadForms();
    }, []);

    const loadForms = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/forms-library');
            const formsData = response.data.data || response.data || [];
            setForms(formsData);
            setFilteredForms(formsData);
            
            // Calculate stats
            const formCount = formsData.filter(f => f.form_type === 'form').length;
            const templateCount = formsData.filter(f => f.form_type === 'template').length;
            setStats({ total: formsData.length, forms: formCount, templates: templateCount });
            
            // Initialize expanded categories
            const expanded = {};
            categories.forEach(cat => {
                if (cat.value !== 'all') expanded[cat.value] = true;
            });
            setExpandedCategories(expanded);
        } catch (err) {
            console.error('Error loading forms:', err);
            setError('Failed to load forms library');
        } finally {
            setLoading(false);
        }
    };

    // Filter forms
    const applyFilters = useCallback(() => {
        let result = [...forms];
        
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(f => 
                f.form_number?.toLowerCase().includes(term) ||
                f.title?.toLowerCase().includes(term) ||
                f.parent_sop_number?.toLowerCase().includes(term)
            );
        }
        
        if (selectedCategory !== 'all') {
            result = result.filter(f => f.category === selectedCategory);
        }
        
        if (selectedType !== 'all') {
            result = result.filter(f => f.form_type === selectedType);
        }
        
        if (selectedSOP !== 'all') {
            result = result.filter(f => f.parent_sop_number === selectedSOP);
        }
        
        setFilteredForms(result);
    }, [forms, searchTerm, selectedCategory, selectedType, selectedSOP]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Get unique parent SOPs
    const parentSOPs = [...new Set(forms.map(f => f.parent_sop_number).filter(Boolean))].sort();

    // Group forms by category
    const groupedForms = filteredForms.reduce((acc, form) => {
        const cat = form.category || 'Uncategorized';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(form);
        return acc;
    }, {});

    // Toggle category expansion
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    // View form
    const handleView = async (form) => {
        try {
            const response = await api.get(`/forms-library/${form.id}/view`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error viewing form:', err);
            alert('Failed to open form');
        }
    };

    // Download form
    const handleDownload = async (form) => {
        try {
            const response = await api.get(`/forms-library/${form.id}/download`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${form.form_number}_v${form.version}.html`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading form:', err);
            alert('Failed to download form');
        }
    };

    // Export for audit
    const handleExport = async () => {
        try {
            const csvContent = [
                ['Form Number', 'Title', 'Type', 'Category', 'Parent SOP', 'Version', 'Effective Date', 'Status'].join(','),
                ...filteredForms.map(f => [
                    f.form_number,
                    `"${f.title}"`,
                    f.form_type,
                    f.category,
                    f.parent_sop_number || '',
                    f.version,
                    new Date(f.effective_date + 'T00:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC'}),
                    f.status
                ].join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `KLINISOL_Forms_Register_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: COLORS.magenta }} />
                    <p className="text-gray-600">Loading Forms Library...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="text-red-600 mb-4">{error}</p>
                    <button onClick={loadForms} className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Unified Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                        {/* Title Section */}
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold">
                                KLIN<span className="text-[#E91E63]">i</span>SOL Document Control System
                            </h1>
                            <p className="text-sm opacity-90 mt-1">Designed for FDA 21 CFR Part 11 Compliance</p>
                        </div>
                        
                        {/* 4 Mini-Card Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate('/sop-library')}
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                            >
                                <FileText className="w-5 h-5" />
                                <span className="hidden sm:inline">SOPs</span>
                            </button>
                            <button
                                onClick={() => navigate('/forms-library')}
                                className="px-4 py-2 bg-white text-[#475569] rounded-lg font-semibold flex items-center gap-2 shadow-md"
                            >
                                <ClipboardList className="w-5 h-5" />
                                <span className="hidden sm:inline">Forms</span>
                            </button>
                            <button
                                onClick={handleExport}
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                            >
                                <Download className="w-5 h-5" />
                                <span className="hidden sm:inline">Export</span>
                            </button>
                            <button 
                                onClick={() => navigate('/dashboard')} 
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                            >
                                <Home className="w-5 h-5" />
                                <span className="hidden sm:inline">Dashboard</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Total Documents</p>
                                <p className="text-2xl font-bold" style={{ color: COLORS.slateGrey }}>{stats.total}</p>
                            </div>
                            <FileText className="w-8 h-8" style={{ color: COLORS.magenta }} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Forms</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.forms}</p>
                            </div>
                            <ClipboardList className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Templates</p>
                                <p className="text-2xl font-bold text-purple-600">{stats.templates}</p>
                            </div>
                            <FileCheck className="w-8 h-8 text-purple-500" />
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search forms..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="flex-1 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                            >
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </select>
                        </div>
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                        >
                            <option value="all">All Types</option>
                            <option value="form">Forms Only</option>
                            <option value="template">Templates Only</option>
                        </select>
                        <select
                            value={selectedSOP}
                            onChange={(e) => setSelectedSOP(e.target.value)}
                            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                        >
                            <option value="all">All Parent SOPs</option>
                            {parentSOPs.map(sop => (
                                <option key={sop} value={sop}>{sop}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Regulatory Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800">
                        <strong>Regulatory Record:</strong> This dynamic register provides real-time access to all controlled forms and templates.
                        Each form is linked to its parent SOP for traceability per FDA 21 CFR Part 11 and ICH-GCP requirements.
                    </p>
                </div>

                {/* Forms by Category */}
                {Object.entries(groupedForms).map(([category, categoryForms]) => (
                    <div key={category} className="bg-white rounded-lg shadow mb-4 overflow-hidden">
                        <button
                            onClick={() => toggleCategory(category)}
                            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                        >
                            <div className="flex items-center">
                                {expandedCategories[category] ? (
                                    <ChevronDown className="w-5 h-5 mr-2" style={{ color: COLORS.slateGrey }} />
                                ) : (
                                    <ChevronRight className="w-5 h-5 mr-2" style={{ color: COLORS.slateGrey }} />
                                )}
                                <span className="font-semibold" style={{ color: COLORS.slateGrey }}>{category}</span>
                                <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                                    {categoryForms.length} {categoryForms.length === 1 ? 'document' : 'documents'}
                                </span>
                            </div>
                        </button>

                        {expandedCategories[category] && (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-t">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Form Number</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Parent SOP</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Version</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Effective</th>
                                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {categoryForms.map(form => (
                                            <tr key={form.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3">
                                                    <span className="font-medium" style={{ color: COLORS.slateGrey }}>
                                                        {form.form_number}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{form.title}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                                                        form.form_type === 'form' 
                                                            ? 'bg-blue-100 text-blue-800' 
                                                            : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                        {form.form_type === 'form' ? 'Form' : 'Template'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {form.parent_sop_number && (
                                                        <button
                                                            onClick={() => navigate(`/sop-library?sop=${form.parent_sop_number}`)}
                                                            className="flex items-center justify-center text-xs text-pink-600 hover:text-pink-800"
                                                        >
                                                            <LinkIcon className="w-3 h-3 mr-1" />
                                                            {form.parent_sop_number}
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center text-sm font-medium" style={{ color: COLORS.magenta }}>
                                                    {form.version}
                                                </td>
                                                <td className="px-4 py-3 text-center text-sm text-gray-500">
                                                    <div className="flex items-center justify-center">
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        {new Date(form.effective_date + 'T00:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC'})}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div className="flex items-center justify-center space-x-2">
                                                        <button
                                                            onClick={() => handleView(form)}
                                                            className="p-1.5 rounded hover:bg-gray-100"
                                                            title="View"
                                                        >
                                                            <Eye className="w-4 h-4" style={{ color: COLORS.slateGrey }} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownload(form)}
                                                            className="p-1.5 rounded hover:bg-gray-100"
                                                            title="Download"
                                                        >
                                                            <Download className="w-4 h-4" style={{ color: COLORS.magenta }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ))}

                {filteredForms.length === 0 && (
                    <div className="bg-white rounded-lg shadow p-8 text-center">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-gray-500">No forms found matching your criteria</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormsLibrary;
